import type { ReactNode } from "react";
import type { Theme } from "src/theme";
type RadioButtonProps = {
  name: string;
  id: string;
  checked: boolean;
  label: string;
  value: string | number;
  onChange: (event: React.SyntheticEvent<HTMLInputElement>) => void;
  testId?: string;
};
const labelWrapperStyle = (theme: Theme) => ({
  position: "relative" as const,
  height: theme.gridUnit * 8,
  lineHeight: `${theme.gridUnit * 5}px`,
  verticalAlign: "middle",
  display: "flex",
  cursor: "pointer"
});
const radioIconStyle = (theme: Theme) => ({
  boxSizing: "border-box" as const,
  display: "inline-block",
  cursor: "pointer",
  border: `${theme.gridUnit}px solid ${theme.colors.grey9}`,
  background: "white",
  borderRadius: "100%",
  marginRight: theme.gridUnit * 2,
  height: theme.gridUnit * 5,
  width: theme.gridUnit * 5,
  "&::before": {
    content: "''",
    margin: "auto"
  }
});
// opacity:0 (not visibility:hidden) keeps the input in the a11y tree and as the
// pointer target; stretched over the label so a click anywhere toggles it.
const radioInputStyles = (theme: Theme) => ({
  position: "absolute" as const,
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  margin: 0,
  opacity: 0,
  cursor: "pointer" as const,
  zIndex: 1,
  "&:checked ~ i": {
    border: `${theme.gridUnit}px solid ${theme.colors.grey6}`,
    boxShadow: `0px 0px ${theme.gridUnit}px ${theme.colors.grey4}`,
    background: theme.colors.grey4
  }
});
const RadioButton = (props: RadioButtonProps): ReactNode => {
  const { label, checked, id, value, onChange, testId } = props;
  return (
    <label htmlFor={id} key={label} css={labelWrapperStyle} data-testid={testId}>
      <input
        type="radio"
        css={radioInputStyles}
        checked={checked}
        id={id}
        value={value}
        name={props.name}
        onChange={onChange}
      />
      <i css={radioIconStyle}></i>
      <span>{label}</span>
    </label>
  );
};

export default RadioButton;
